import React, { useState, useRef, useEffect, useCallback } from 'react'

const BRAND_BLUE = '#185FA5'

function getCounterState(count, max) {
  if (count >= max) return 'danger'
  if (count >= max * 0.83) return 'warning'
  return 'safe'
}

function autoResize(textarea) {
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

function getChipLabel(chipId, chips) {
  const found = chips.find((c) => c.id === chipId)
  return found?.label ?? chipId
}

function ChipButton({ chip, selected, disabled, atMaxSelections, onClick }) {
  const [shaking, setShaking] = useState(false)

  const handleClick = () => {
    if (disabled) return
    if (!selected && atMaxSelections) {
      setShaking(true)
      setTimeout(() => setShaking(false), 250)
      return
    }
    onClick()
  }

  const baseStyle = {
    fontSize: 11,
    padding: '4px 9px',
    borderRadius: 12,
    border: 'none',
    cursor: disabled ? 'default' : 'pointer',
    whiteSpace: 'nowrap',
    fontFamily: 'inherit',
    fontWeight: selected ? 500 : 400,
    transition: 'background var(--duration-fast) ease, color var(--duration-fast) ease',
  }

  const selectedStyle = {
    background: BRAND_BLUE,
    color: 'white',
  }

  const unselectedStyle = {
    background: '#f1efe8',
    color: '#5F5E5A',
  }

  const disabledOverlay = !selected && atMaxSelections ? { opacity: 0.5 } : {}
  const fullDisabledStyle = disabled ? { opacity: 0.4 } : {}

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      aria-pressed={selected}
      aria-label={`${chip.label}${selected ? ', selected' : ''}`}
      className={shaking ? 'chip-shake' : ''}
      style={{
        ...baseStyle,
        ...(selected ? selectedStyle : unselectedStyle),
        ...disabledOverlay,
        ...fullDisabledStyle,
      }}
    >
      {chip.label}
    </button>
  )
}

function ContextBlock({ chipLabel, context, maxChars, disabled, onChange }) {
  const textareaRef = useRef(null)
  const charCount = context.length
  const counterState = getCounterState(charCount, maxChars)

  const counterColor = {
    safe: 'var(--text-secondary, #999)',
    warning: '#BA7517',
    danger: '#A32D2D',
  }[counterState]

  useEffect(() => {
    if (textareaRef.current) autoResize(textareaRef.current)
  }, [context])

  return (
    <div
      style={{
        borderLeft: `2px solid ${BRAND_BLUE}`,
        paddingLeft: 10,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 4,
        }}
      >
        <span
          style={{
            fontSize: 10,
            fontWeight: 500,
            color: BRAND_BLUE,
            letterSpacing: '0.03em',
            textTransform: 'uppercase',
          }}
        >
          {chipLabel}
        </span>
        <span
          aria-live="polite"
          style={{
            fontSize: 10,
            color: counterColor,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {charCount} / {maxChars}
        </span>
      </div>

      <textarea
        ref={textareaRef}
        value={context}
        onChange={(e) => {
          const next = e.target.value
          if (next.length <= maxChars) {
            onChange(next)
          } else {
            onChange(next.slice(0, maxChars))
          }
        }}
        placeholder="Add context…"
        disabled={disabled}
        rows={1}
        aria-label={`Context for ${chipLabel}`}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          background: 'transparent',
          border: 'none',
          outline: 'none',
          resize: 'none',
          fontFamily: 'inherit',
          fontSize: 12,
          lineHeight: 1.5,
          color: 'var(--text-primary)',
          padding: 0,
          overflowY: 'hidden',
          minHeight: '18px',
        }}
        onInput={(e) => autoResize(e.target)}
      />
    </div>
  )
}

export function ChipMultiSelectWithContext({
  value,
  onChange,
  chips,
  maxSelections = 2,
  maxContextChars = 120,
  disabled = false,
}) {
  const selections = value?.selections ?? []
  const atMaxSelections = selections.length >= maxSelections

  const handleChipClick = useCallback(
    (chipId) => {
      const existing = selections.find((s) => s.chipId === chipId)

      if (existing) {
        // Deselect
        if (existing.context.trim().length > 0) {
          const chipLabel = getChipLabel(chipId, chips)
          const confirmed = window.confirm(`Remove "${chipLabel}" and its context?`)
          if (!confirmed) return
        }
        onChange({ selections: selections.filter((s) => s.chipId !== chipId) })
      } else {
        // Select
        if (atMaxSelections) return
        onChange({
          selections: [
            ...selections,
            { chipId, context: '', selectedAt: new Date().toISOString() },
          ],
        })
      }
    },
    [selections, chips, onChange, atMaxSelections],
  )

  const handleContextChange = useCallback(
    (chipId, newContext) => {
      onChange({
        selections: selections.map((s) => (s.chipId === chipId ? { ...s, context: newContext } : s)),
      })
    },
    [selections, onChange],
  )

  const isSelected = (chipId) => selections.some((s) => s.chipId === chipId)

  return (
    <div style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: 10 }}>
      <style>{`
        @keyframes chip-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-3px); }
          50% { transform: translateX(3px); }
          75% { transform: translateX(-2px); }
        }
        @media (prefers-reduced-motion: no-preference) {
          .chip-shake { animation: chip-shake 200ms ease-in-out; }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 5,
          rowGap: 6,
        }}
      >
        {chips.map((chip) => (
          <ChipButton
            key={chip.id}
            chip={chip}
            selected={isSelected(chip.id)}
            disabled={disabled}
            atMaxSelections={atMaxSelections}
            onClick={() => handleChipClick(chip.id)}
          />
        ))}
      </div>

      {selections.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {selections.map((selection) => (
            <ContextBlock
              key={selection.chipId}
              chipLabel={getChipLabel(selection.chipId, chips)}
              context={selection.context}
              maxChars={maxContextChars}
              disabled={disabled}
              onChange={(newContext) => handleContextChange(selection.chipId, newContext)}
            />
          ))}
        </div>
      )}
    </div>
  )
}
